import Handsontable from 'handsontable';
import { foldToAs400Ascii } from '@erp/rpc';
import type { As400NumberFormat } from '@erp/rpc';
import { asCellText } from './as400ExportParts';
import {
  applyAs400NumberDisplay,
  as400NumberFormatForProp,
} from './as400ReviewGridDisplay';
import {
  cellTint,
  isGridRecord,
  carriedWithChange,
  type As400GridRecord,
} from './as400ExportEditState';
import {
  attachItemSiblings,
  cellViolations,
  correctedSlashCount,
  fieldsAffectedBy,
  isBlocking,
  isDraftRowKey,
  type CellAutoFill,
  type CellRow,
  type CellRulesByField,
  type CellViolation,
} from '@erp/item-master-rules';
import {
  gridRecordCellRow,
  type PendingCellViolation,
} from './as400GridCellRules';
import { autoFillChanges } from './as400CellAutoFill';

/**
 * The review grid's side of cell validation (GEN-2748): marking cells that
 * break a rule as they render, and finding what a committed change broke.
 * Both read the grid's own source records, never React state — Handsontable
 * renders outside React, and a record always holds what its row shows.
 */

// Inline, like the tints: Handsontable renders outside Tailwind's pipeline.
// An inset outline rather than a fill, so the pending/staged tint underneath
// still reads.
const INVALID_CELL_OUTLINE = 'inset 0 0 0 2px rgb(220, 38, 38)';
// A warning is amber: worth a second look, but nothing Save refuses.
const WARNING_CELL_OUTLINE = 'inset 0 0 0 2px rgb(217, 119, 6)';

/** Marks a rendered cell that breaks a rule. */
export const INVALID_CELL_ATTR = 'data-cell-invalid';
/** Marks a rendered cell whose value only draws a warning. */
export const WARNING_CELL_ATTR = 'data-cell-warning';
/** Marks a rendered cell whose spaces are shown (see showSpaces). */
export const SHOWN_SPACES_ATTR = 'data-cell-spaces';
/** What a shown space renders as: a middle dot, one monospace cell wide. */
export const SPACE_MARK = '\u00B7';
const SPACE_MARK_COLOR = 'rgb(148, 163, 184)';
/** Marks the open cell editor while the value in it breaks a rule. */
export const INVALID_EDITOR_ATTR = 'data-editor-invalid';

/** The outline for a set of violations: red if any blocks Save, else amber. */
function outlineFor(violations: readonly CellViolation[]): string {
  if (violations.length === 0) return '';
  return violations.some(isBlocking)
    ? INVALID_CELL_OUTLINE
    : WARNING_CELL_OUTLINE;
}

/**
 * Outline a cell that breaks a rule (red) or draws a warning (amber) and put
 * what is wrong in its tooltip, or clear both. Handsontable recycles cells, so
 * every render does one or the other.
 */
function markCell(
  td: HTMLTableCellElement,
  violations: readonly CellViolation[],
  describe: (violation: CellViolation) => string,
): void {
  td.style.boxShadow = outlineFor(violations);
  td.removeAttribute(INVALID_CELL_ATTR);
  td.removeAttribute(WARNING_CELL_ATTR);
  td.removeAttribute('aria-invalid');
  td.removeAttribute('title');
  if (violations.length === 0) return;
  if (violations.some(isBlocking)) {
    td.setAttribute(INVALID_CELL_ATTR, '');
    td.setAttribute('aria-invalid', 'true');
  } else {
    td.setAttribute(WARNING_CELL_ATTR, '');
  }
  td.title = violations.map(describe).join('\n');
}

/** Names a run of `count` spaces for assistive tech: "1 space", "3 spaces". */
export type SpaceLabel = (count: number) => string;

/**
 * Show each space in a cell whose spaces are data (a column the grid doesn't
 * trim, see `keepWhitespace`) as a muted middle dot, so `\u00B7N` reads as
 * "a space, then N" at a glance rather than as `N`. Each run of dots is an
 * image named by `spaceLabel` ("1 space"), since a screen reader would skip
 * the spaces themselves and read `" N"` as `N`. The cell's value is
 * untouched: this replaces only what the text renderer drew.
 */
function showSpaces(
  td: HTMLTableCellElement,
  text: string,
  spaceLabel: SpaceLabel,
): void {
  td.setAttribute(SHOWN_SPACES_ATTR, '');
  if (!text.includes(' ')) return;
  td.textContent = '';
  for (const part of text.split(/( +)/)) {
    if (part === '') continue;
    if (!part.startsWith(' ')) {
      td.append(part);
      continue;
    }
    const mark = document.createElement('span');
    mark.style.color = SPACE_MARK_COLOR;
    mark.setAttribute('role', 'img');
    mark.setAttribute('aria-label', spaceLabel(part.length));
    mark.textContent = SPACE_MARK.repeat(part.length);
    td.append(mark);
  }
}

/** The source record behind a visual row, when it is one of ours. */
export function recordAt(instance: Handsontable, rowIndex: number) {
  const record = instance.getSourceDataAtRow(instance.toPhysicalRow(rowIndex));
  return isGridRecord(record) ? record : null;
}

function buildPageCellRows(
  instance: Handsontable,
): ReadonlyMap<string, CellRow> {
  const source = instance.getSourceData();
  const rows = new Map<string, CellRow>();
  if (!Array.isArray(source)) return rows;
  for (const record of source) {
    if (isGridRecord(record)) {
      rows.set(record.__key, gridRecordCellRow(record));
    }
  }
  return attachItemSiblings(rows);
}

// The page's rows, keyed and with their item siblings attached, built once
// per render pass rather than once per cell. `getSourceData()` walks every
// row and column of the page, and the renderer below runs for every visible
// cell: building this map in each call made a cell commit block the page for
// over a second on a 50-row Rexel review (GEN-2729 wired the sibling lookup
// into the renderer). The map goes stale when a value changes, so it is
// dropped before each view render (Handsontable's `beforeViewRender`, hooked
// on first use) and before `committedViolations` reads it — that runs from
// `afterChange`, after the change has landed and before the render that
// follows it. The open editor's check reads it too, and sees the same values
// the last render drew, which is what it compares the typed value against.
const pageCellRowsByInstance = new WeakMap<
  Handsontable,
  ReadonlyMap<string, CellRow>
>();
const invalidationHooked = new WeakSet<Handsontable>();

function forgetPageCellRows(instance: Handsontable): void {
  pageCellRowsByInstance.delete(instance);
}

function pageCellRows(instance: Handsontable): ReadonlyMap<string, CellRow> {
  if (!invalidationHooked.has(instance)) {
    invalidationHooked.add(instance);
    instance.addHook('beforeViewRender', () => forgetPageCellRows(instance));
  }
  const cached = pageCellRowsByInstance.get(instance);
  if (cached) return cached;
  const rows = buildPageCellRows(instance);
  pageCellRowsByInstance.set(instance, rows);
  return rows;
}

function cellRowAt(
  instance: Handsontable,
  record: ReturnType<typeof recordAt>,
): CellRow | null {
  if (!record) return null;
  return pageCellRows(instance).get(record.__key) ?? gridRecordCellRow(record);
}

const NO_NUMBER_FORMATS: ReadonlyMap<string, As400NumberFormat> = new Map();

function paintReviewGridCellText(
  td: HTMLTableCellElement,
  text: string | null,
  keepWhitespace: boolean,
  spaceLabel: SpaceLabel,
  format: As400NumberFormat | undefined,
): void {
  if (keepWhitespace && text !== null) {
    showSpaces(td, text, spaceLabel);
    return;
  }
  td.removeAttribute(SHOWN_SPACES_ATTR);
  applyAs400NumberDisplay(td, text, format);
}

function isUntouchedDraftRow(record: As400GridRecord, row: CellRow): boolean {
  return (
    isDraftRowKey(record.__key) &&
    !Object.keys(record.__persisted).some((field) => row.edited(field))
  );
}

function paintReviewGridCellState(
  td: HTMLTableCellElement,
  instance: Handsontable,
  rowIndex: number,
  prop: string | number,
  text: string | null,
  rules: CellRulesByField,
  describe: (violation: CellViolation) => string,
): void {
  const record = recordAt(instance, rowIndex);
  if (!record || typeof prop !== 'string') {
    markCell(td, [], describe);
    return;
  }
  const row = cellRowAt(instance, record) ?? gridRecordCellRow(record);
  td.style.backgroundColor = cellTint(
    text,
    record.__persisted[prop] ?? null,
    record.__tintBase[prop],
    carriedWithChange(record, prop),
  );
  markCell(
    td,
    isUntouchedDraftRow(record, row) ? [] : cellViolations(rules, prop, row),
    describe,
  );
}

/**
 * The grid's cell renderer: the default text renderer, tinted by edit state
 * (see cellTint) and outlined where the cell breaks one of `rules`, with
 * `describe`'s text as its tooltip.
 */
export function editStateRendererFor(
  rules: CellRulesByField,
  describe: (violation: CellViolation) => string,
  spaceLabel: SpaceLabel,
  numberFormats: ReadonlyMap<string, As400NumberFormat> = NO_NUMBER_FORMATS,
) {
  return function editStateRenderer(
    instance: Handsontable,
    td: HTMLTableCellElement,
    rowIndex: number,
    colIndex: number,
    prop: string | number,
    value: unknown,
    cellProperties: Handsontable.CellProperties,
  ): void {
    Handsontable.renderers.TextRenderer(
      instance,
      td,
      rowIndex,
      colIndex,
      prop,
      value,
      cellProperties,
    );
    const text = asCellText(value);
    paintReviewGridCellText(
      td,
      text,
      cellProperties.trimWhitespace === false,
      spaceLabel,
      as400NumberFormatForProp(numberFormats, prop),
    );
    paintReviewGridCellState(
      td,
      instance,
      rowIndex,
      prop,
      text,
      rules,
      describe,
    );
  };
}

/**
 * What the value in the open cell editor would break if committed now: the
 * editor's cell with `text` in place — normalised as the commit will normalise
 * it — and edited, over the rest of its record, so a cross-field rule reads the
 * row as it stands. The worksheet's `autoFills` land too, as the commit will
 * land them, so typing S in PROD REC STATUS isn't flagged for the CHANGE
 * STATUS FLAG it is about to fill. Local rules only; nothing here goes to the
 * server.
 */
export function editorViolations(
  instance: Handsontable,
  rules: CellRulesByField,
  text: string,
  autoFills: readonly CellAutoFill[] = [],
  blankMarkerFor?: (prop: string) => string | undefined,
): CellViolation[] {
  const editor = instance.getActiveEditor();
  if (!editor || rules.size === 0) return [];
  const prop = instance.colToProp(editor.col);
  const record = recordAt(instance, editor.row);
  if (!record || typeof prop !== 'string') return [];
  // Trimmed as the commit will trim it, unless this column keeps its spaces.
  const keepSpaces = editor.cellProperties.trimWhitespace === false;
  const typed = keepSpaces ? text : text.trim();
  const folded = asCellText(foldToAs400Ascii(typed.toUpperCase()));
  const marker = blankMarkerFor?.(prop);
  const value =
    folded !== null && marker
      ? (correctedSlashCount(folded, marker) ?? folded)
      : folded;
  const rawValue = asCellText(foldToAs400Ascii(text.toUpperCase()));
  const committed =
    pageCellRows(instance).get(record.__key) ?? gridRecordCellRow(record);
  const typedChange = { field: prop, value };
  const landing = new Map(
    [typedChange, ...autoFillChanges(autoFills, [typedChange], committed)].map(
      (change) => [change.field, change.value],
    ),
  );
  const live: CellRow = {
    value: (field) =>
      landing.has(field)
        ? (landing.get(field) ?? null)
        : committed.value(field),
    edited: (field) => landing.has(field) || committed.edited(field),
    mode: committed.mode,
    itemAdds: committed.itemAdds,
    stockUoms: committed.stockUoms,
    siblings: committed.siblings,
  };
  const normalized = fieldsAffectedBy(rules, prop).flatMap((field) =>
    cellViolations(rules, field, live),
  );
  if (rawValue === value) return normalized;
  const rawLive: CellRow = {
    ...live,
    value: (field) => (field === prop ? rawValue : live.value(field)),
  };
  return includeRawWhitespaceWarnings(
    normalized,
    cellViolations(rules, prop, rawLive),
  );
}

export function includeRawWhitespaceWarnings(
  normalized: readonly CellViolation[],
  raw: readonly CellViolation[],
): CellViolation[] {
  return [
    ...normalized.filter(
      (violation) => violation.detail.rule !== 'whitespace-only',
    ),
    ...raw.filter((violation) => violation.detail.rule === 'whitespace-only'),
  ];
}

/**
 * Outline the open editor while its value breaks a rule, in the same red as a
 * broken cell (amber when it only draws a warning: `blocking` false), with
 * `noteId` (the element saying what is wrong) as what describes it to
 * assistive tech — or hand it back to Handsontable's own outline.
 */
export function markEditor(
  editor: HTMLTextAreaElement,
  noteId: string | null,
  blocking = true,
): void {
  const outline = blocking ? INVALID_CELL_OUTLINE : WARNING_CELL_OUTLINE;
  editor.style.boxShadow = noteId ? outline : '';
  if (noteId) {
    editor.setAttribute(INVALID_EDITOR_ATTR, '');
    if (blocking) editor.setAttribute('aria-invalid', 'true');
    else editor.removeAttribute('aria-invalid');
    editor.setAttribute('aria-describedby', noteId);
    return;
  }
  editor.removeAttribute(INVALID_EDITOR_ATTR);
  editor.removeAttribute('aria-invalid');
  editor.removeAttribute('aria-describedby');
}

/** The open cell editor, when `target` is it. */
export function cellEditorFrom(
  target: EventTarget | null,
): HTMLTextAreaElement | null {
  return target instanceof HTMLTextAreaElement &&
    target.classList.contains('handsontableInput')
    ? target
    : null;
}

/**
 * What a batch of committed changes should say in a popup: nothing for the
 * open editor's own commit while its note is showing — the author was told as
 * they typed — else what the changes broke ({@link committedViolations}).
 */
export function violationsToAnnounce(
  instance: Handsontable,
  changes: readonly Handsontable.CellChange[],
  rules: CellRulesByField,
  source: string,
  editorNoteShown: boolean,
): PendingCellViolation[] {
  if (source === 'edit' && editorNoteShown) return [];
  return committedViolations(instance, changes, rules);
}

/**
 * What a batch of committed cell changes broke, each with the row it is on:
 * each changed cell, plus every cell whose rule reads a changed field, checked
 * once each. Runs after the change has landed, so it sees what the grid now
 * shows.
 */
export function committedViolations(
  instance: Handsontable,
  changes: readonly Handsontable.CellChange[],
  rules: CellRulesByField,
): PendingCellViolation[] {
  if (rules.size === 0) return [];
  // The changes have landed in the source records but the grid has not
  // redrawn yet: read the rows as they are now, not as they were drawn.
  forgetPageCellRows(instance);
  const checked = new Set<string>();
  const violations: PendingCellViolation[] = [];
  for (const [rowIndex, prop] of changes) {
    const record = recordAt(instance, rowIndex);
    if (!record || typeof prop !== 'string') continue;
    const row = cellRowAt(instance, record) ?? gridRecordCellRow(record);
    for (const field of fieldsAffectedBy(rules, prop)) {
      const key = `${record.__key}\n${field}`;
      if (checked.has(key)) continue;
      checked.add(key);
      for (const violation of cellViolations(rules, field, row)) {
        violations.push({ rowKey: record.__key, violation });
      }
    }
  }
  return violations;
}
